/**
 * Fake match-context fillers — for fields the partner-api doesn't yet push
 * onto clip metadata (scoreline, match stage, opponent team, situational
 * highlight tags).
 *
 * Deterministic per clip._id so renders don't flicker between re-renders.
 * Drop this file once partner-api ingestion emits the real `CS_*` tag set
 * (Kill Count, Headshots, Situational, Weapons, Map, Round) and the match
 * context fields land on PlaylistClip.metadata.
 */
import { getTagBadges } from "MVVM/Utilities/ClipTags";

import type { PlaylistClip } from "../../@types";
import type { ClipMatchContext } from "./@types";
import { getTeamName } from "./metadataHelpers";

const MOCK_STAGES = [
  "Group Stage",
  "Quarter Finals",
  "Semi Finals",
  "Grand Final",
  "Upper Bracket",
  "Lower Bracket",
];

// Active CS2 map pool — engine doesn't ingest map name on clip metadata yet,
// so titles like "donk's ace clutch on Nuke" pull from this deterministic pool.
const MOCK_MAPS = [
  "Mirage",
  "Inferno",
  "Nuke",
  "Anubis",
  "Ancient",
  "Vertigo",
  "Dust 2",
];

// Real HLTV team logo URLs aren't wired through partner-api yet. Inline
// sentinels resolve to colored initial-letter crests via <TeamCrest>.
const MOCK_OPPONENTS = [
  { name: "FaZe", logo: "inline:team-faze" },
  { name: "NAVI", logo: "inline:team-navi" },
  { name: "Vitality", logo: "inline:team-vitality" },
  { name: "G2", logo: "inline:team-g2" },
  { name: "Spirit", logo: "inline:team-spirit" },
  { name: "MOUZ", logo: "inline:team-mouz" },
];

const hashString = (s: string): number => {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

/**
 * Pool of `CS_*` tag bundles that drive the canonical `getTagBadges`
 * pipeline (MVVM/Utilities/ClipTags). Each entry is the SAME shape real
 * clips emit from partner-api once ingestion catches up — keeping the
 * mock and the real path on one code path means the rendered badges
 * (gold-shimmer ACE, weapon icons, 1VX clutches, etc.) are identical to
 * what match-history shows everywhere else in the app.
 *
 * Combos are picked deterministically by clip._id hash; each one yields
 * 1–2 badges from `getTagBadges` after weight-sorting.
 */
const MOCK_CS_TAG_COMBOS: { key: string; value: string }[][] = [
  // Ace + all headshots — gold shimmer + "ALL HEADSHOTS" highlight
  [
    { key: "CS_Kill Count", value: "5" },
    { key: "CS_Headshots", value: "5" },
  ],
  // 4 kills + AWP — "4 KILLS" featured + AWP weapon-icon pill
  [
    { key: "CS_Kill Count", value: "4" },
    { key: "CS_Weapons", value: "awp" },
  ],
  // Noscope AWP
  [
    { key: "CS_Situational", value: "noscope" },
    { key: "CS_Weapons", value: "awp" },
  ],
  // 1v4 clutch (highlight + shimmer)
  [{ key: "CS_Situational", value: "1v4" }],
  // 4 headshots + AWP
  [
    { key: "CS_Headshots", value: "4" },
    { key: "CS_Weapons", value: "awp" },
  ],
  // 3 headshots
  [{ key: "CS_Headshots", value: "3" }],
  // Ace + wallbang — gold + featured wallbang
  [
    { key: "CS_Kill Count", value: "5" },
    { key: "CS_Situational", value: "wallbang" },
  ],
  // 1v2 clutch
  [{ key: "CS_Situational", value: "1v2" }],
  // Ace + 1v3 — gold + featured 1V3 highlight
  [
    { key: "CS_Kill Count", value: "5" },
    { key: "CS_Situational", value: "1v3" },
  ],
];

const seedFor = (clip: PlaylistClip | null): number =>
  hashString(clip?._id ?? clip?.shareId ?? "fallback");

/**
 * Per-clip mock TClipTag bundle in the canonical partner-api shape.
 * Feed to `getTagBadges` to get the same badge objects match-history uses.
 */
export const buildMockTClipTags = (
  clip: PlaylistClip | null,
): { key: string; value: string }[] => {
  const seed = seedFor(clip);
  return MOCK_CS_TAG_COMBOS[seed % MOCK_CS_TAG_COMBOS.length];
};

/**
 * Label-only convenience for surfaces that just want the headline tag
 * (e.g. NowPlayingHeader's "top tag" pill). Pulls the highest-weight badge
 * from `getTagBadges` so the answer always reflects the canonical badge
 * vocabulary (ACE / 4 KILLS / 1V4 / etc.) instead of a parallel mock list.
 */
export const buildMockTopTagLabel = (
  clip: PlaylistClip | null,
): string | null => {
  const badges = getTagBadges(buildMockTClipTags(clip));
  return badges[0]?.label ?? null;
};

export const buildMockMatchContext = (
  clip: PlaylistClip | null,
): ClipMatchContext => {
  const seed = seedFor(clip);
  const stage = MOCK_STAGES[seed % MOCK_STAGES.length];

  const opponentPool = MOCK_OPPONENTS.filter(
    (t) => t.name.toLowerCase() !== getTeamName(clip).toLowerCase(),
  );
  const opponent =
    opponentPool[seed % opponentPool.length] ?? MOCK_OPPONENTS[0];

  // Plausible scorelines: 16-X with X in [4..14]
  const losingScore = 4 + (seed % 11);
  const scoreline = `16 - ${losingScore}`;

  // Series game N of 3 (BO3 most common in HLTV)
  const total = 3;
  const game = (seed % total) + 1;
  const seriesGameLabel = `Game ${game} of ${total}`;

  // Map + round number — both gaps in current engine ingestion. Round is
  // 1..30 (CS2 max-round in regulation MR12 is 24, MR15 legacy is 30; pick a
  // late-round-ish flavor by biasing into 5..28).
  const mapName = MOCK_MAPS[seed % MOCK_MAPS.length];
  const roundNumber = 5 + (seed % 24);

  return {
    matchStage: stage,
    scoreline,
    seriesGameLabel,
    opponentTeamName: opponent.name,
    opponentTeamLogo: opponent.logo,
    mapName,
    roundNumber,
  };
};
